import { Children, ReactNode, cloneElement, isValidElement, useLayoutEffect, useMemo, useRef } from "react"
import gsap from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import parse, { HTMLReactParserOptions } from "html-react-parser"

gsap.registerPlugin(ScrollTrigger)

type BaseRevealTextType = {
    text: string
    parseOptions?: HTMLReactParserOptions
    topContent?: ReactNode
    wrapperClassProps?: string
    sectionClassProps?: string
    contentClassProps?: string
    textClassProps?: string
    charClassName?: string
}

const defaultWrapperClass = "w-full py-48"
const defaultSectionClass = "px-4 sm:px-8 xl:px-0"
const defaultContentClass = "max-w-page mx-auto flex flex-col items-center justify-center text-center gap-6 normal"
const defaultTextClass = "max-w-5xl text-4xl leading-snug font-text tracking-wide normal-case font-light text-white text-center grow"

export default function BaseRevealText({
    text,
    parseOptions,
    topContent,
    wrapperClassProps,
    sectionClassProps,
    contentClassProps,
    textClassProps,
    charClassName,
}: BaseRevealTextType) {
    const sectionRef = useRef<HTMLElement | null>(null)

    const parsedText = useMemo(() => {
        return parse(text ?? "", parseOptions)
    }, [text, parseOptions])

    const revealClass = charClassName ?? "base-reveal-char"

    const renderRevealNode = (node: ReactNode, path = "root", inheritedClassName = ""): ReactNode => {
        if (typeof node === "string") {
            return node.split(/(\s+)/).map((token, tokenIndex) => {
                if (!token.length) return null

                if (/^\s+$/.test(token)) {
                    return (
                        <span key={`${path}-space-${tokenIndex}`} className="inline-block whitespace-pre">
                            {token}
                        </span>
                    )
                }

                return (
                    <span key={`${path}-word-${tokenIndex}`} className="inline-block whitespace-nowrap">
                        {token.split("").map((char, charIndex) => (
                            <span
                                key={`${path}-${tokenIndex}-${charIndex}`}
                                className={`${revealClass} inline-block ${inheritedClassName}`.trim()}>
                                {char}
                            </span>
                        ))}
                    </span>
                )
            })
        }

        if (Array.isArray(node)) {
            return node.map((child, index) => renderRevealNode(child, `${path}-${index}`, inheritedClassName))
        }

        if (isValidElement<{ className?: string; children?: ReactNode }>(node)) {
            const ownClassName = typeof node.props.className === "string" ? node.props.className : ""
            const mergedClassName = `${inheritedClassName} ${ownClassName}`.trim()

            return cloneElement(
                node,
                {
                    key: path,
                    className: ownClassName,
                },
                Children.map(node.props.children, (child, index) => renderRevealNode(child, `${path}-${index}`, mergedClassName))
            )
        }

        return node
    }

    useLayoutEffect(() => {
        if (!sectionRef.current || !text?.length) return

        const ctx = gsap.context(() => {
            const chars = gsap.utils.toArray<HTMLElement>(`.${revealClass}`)

            gsap.set(chars, { opacity: 0.1 })

            gsap.timeline({
                scrollTrigger: {
                    trigger: sectionRef.current,
                    start: "top 80%",
                    end: "bottom 45%",
                    scrub: true,
                    invalidateOnRefresh: true,
                    refreshPriority: -1,
                },
            }).to(chars, {
                opacity: 1,
                stagger: 0.025,
                ease: "none",
            })
        }, sectionRef)

        const refreshOnFontsReady = async () => {
            await document.fonts?.ready
            ScrollTrigger.refresh()
        }
        refreshOnFontsReady()

        return () => {
            ctx.revert()
        }
    }, [text, revealClass])

    return (
        <div className={`${defaultWrapperClass} ${wrapperClassProps ?? ""}`.trim()}>
            <section ref={sectionRef} className={`${defaultSectionClass} ${sectionClassProps ?? ""}`.trim()}>
                <div className={`${defaultContentClass} ${contentClassProps ?? ""}`.trim()}>
                    {topContent}
                    <div className={`${defaultTextClass} ${textClassProps ?? ""}`.trim()}>{renderRevealNode(parsedText)}</div>
                </div>
            </section>
        </div>
    )
}
